import { t } from 'i18next';

import { createScreenshotOfThreeObject } from '../../../../../../utils/three/snapshot';
import { isDefined } from '../../../../../../utils/variables';
import {
  PrismData,
  PrismVolumeModel,
} from '../../../../../../viewer/elements/volume/model/prism-volume-model';
import { VolumeModelState } from '../../../../../../viewer/elements/volume/types';
import { ProgressController } from '../../../../../utils/progress-controller';
import { VolumeWithAsset } from '../helpers/utils';
import { maxPrismCountPrismReport } from './prism-pdf';

export type LoadedPrismData = { id: string; data: PrismData[] };
export type LoadedOverviewUrl = { id: string; url: string };
export type ReportOptions = {
  includeOverview: boolean;
  includePrismList: boolean;
  progressController: ProgressController;
};

/**
 * Load data that the Prism Report depends on optionally depending on the settings.
 */
export async function loadOptionalPrismVolumeReportData(
  selectedVolume: VolumeWithAsset | undefined,
  prismData: LoadedPrismData | undefined,
  overviewUrl: LoadedOverviewUrl | undefined,
  options: ReportOptions,
) {
  if (!options.includeOverview && !options.includePrismList) {
    return undefined;
  }

  const model = await loadPrismVolumeModel(selectedVolume, options.progressController);

  if (!isDefined(model)) {
    return undefined;
  }

  return {
    prismData: loadPrismData(model, selectedVolume, prismData, options),
    overviewUrl: loadOverviewUrl(model, selectedVolume, overviewUrl, options),
  };
}

function loadPrismData(
  model: PrismVolumeModel,
  selectedVolume: VolumeWithAsset | undefined,
  prismData: LoadedPrismData | undefined,
  options: ReportOptions,
): LoadedPrismData | undefined {
  if (!isDefined(selectedVolume?.volume?.id) || !options.includePrismList) {
    return undefined;
  }

  if (prismData?.id === selectedVolume?.volume.id) {
    return { ...prismData };
  }

  const newPrismData = model.getPrismData(0, maxPrismCountPrismReport);

  return { id: selectedVolume.volume.id, data: newPrismData };
}

function loadOverviewUrl(
  model: PrismVolumeModel,
  selectedVolume: VolumeWithAsset | undefined,
  overviewUrl: LoadedOverviewUrl | undefined,
  options: ReportOptions,
): LoadedOverviewUrl | undefined {
  if (!isDefined(selectedVolume?.volume?.id) || !options.includeOverview) {
    return undefined;
  }

  if (overviewUrl?.id === selectedVolume.volume.id) {
    return { ...overviewUrl };
  }

  // Approximately page width in landscape mode for a 72 dpi PDF document.
  const imageSize = 400;
  const dataUrl = createScreenshotOfThreeObject(model.gltfModel, imageSize);

  return { id: selectedVolume.volume.id, url: dataUrl };
}

async function loadPrismVolumeModel(
  selectedVolume: VolumeWithAsset | undefined,
  progressController: ProgressController,
) {
  if (!isDefined(selectedVolume)) {
    return undefined;
  }

  progressController.callback(1, t('prismReport.loadingVolume', { ns: 'skyviewReport' }) as string);
  const wasVisibleBefore =
    selectedVolume.asset.getStateVisibility() &&
    selectedVolume.asset.volumeHandler.isModelVisible();

  selectedVolume.asset.volumeHandler.activeVolume = selectedVolume.volume;
  selectedVolume.asset.volumeHandler.setModelState(VolumeModelState.Hidden);

  // If another volume model is loaded we dispose it before loading the new model.
  if (selectedVolume.asset.volumeHandler.volumeModel?.volumeId !== selectedVolume.volume.id) {
    selectedVolume.asset.volumeHandler.clearVolumeModel();
  }

  await selectedVolume.asset.volumeHandler.toggleModelVisibility();

  // Make sure the new visibility state is the same as the state before.
  selectedVolume.asset.volumeHandler.volumeModel!.visible = wasVisibleBefore;
  selectedVolume.asset.volumeHandler.setModelState(
    wasVisibleBefore ? VolumeModelState.Visible : VolumeModelState.Hidden,
  );

  if (
    !(
      selectedVolume.asset.volumeHandler.volumeModel instanceof PrismVolumeModel &&
      selectedVolume.asset.volumeHandler.activeVolume === selectedVolume.volume
    )
  ) {
    throw new Error('Volume failed to load.');
  }

  return selectedVolume.asset.volumeHandler.volumeModel;
}
